import { type FC, memo, useEffect, useMemo } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { AddFeature, RecordParticipationTypes, RemoveFeature, useSimpleFeatureTernary } from "@viagogo/feature";
import { TruncateText } from "src/shared/components/TruncateText";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { isNaCobrands } from "src/shared/utils/appContextHelper";
import { GenreTags } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/GenreTags";
import { PerformerCardSocialLinks } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/PerformerCardSocialLinks";
import { ReigningChampion } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/reigning-champion/ReigningChampion";
import {
  genreTagContainerStyle,
  performerCardSection,
  performerInfoCard,
  socialLinksStyle,
  spotifyPlayerStyle,
  theaterRuntimeMargin,
  videoContainerStyle,
} from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/styles";
import { TheaterRuntime } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/theater-runtime/TheaterRuntime";
import { UserContentCarousel } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/user-generated-content/UserContentCarousel";
import { EmbeddedYoutubePlayer } from "src/website-apps/responsive/stubhub-performer/components/performer-youtube-player";
import { SpotifyEmbeddedPlayer } from "src/website-apps/responsive/stubhub-performer/components/spotify-player";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { usePerformerInfoCardData } from "src/website-apps/shared/hooks/usePerformerInfoCardData";

const cardClasses = performerInfoCard({ cardBorder: "bordered" });
const sectionMutedClasses = performerCardSection({ tone: "muted" });
const sectionMutedSmallClasses = performerCardSection({ tone: "muted", size: "small" });

/**
 * Card to display information about a performer such as name, bio, social media links, etc.
 */
export const PerformerInfoCardMobile: FC = memo(() => {
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const model = usePerformerPageModelData();
  const isTabletBreakpoint = useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const isMobile = !isTabletBreakpoint;
  const isTablet = isTabletBreakpoint && !isDesktop;
  const {
    hasEnoughData,
    genres,
    bio,
    spotifyTopTrackId,
    youTubeVideoId,
    showDuration,
    catalogSourcedGenre,
    hasReigningChampionData,
    reigningChampions,
    categoryUserGeneratedImages,
  } = usePerformerInfoCardData();

  const maxLinesForBio = useMemo(() => {
    if (isMobile) return 21;
    if (isTablet) return 10;
    return 5;
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (hasEnoughData) {
      // record when loaded so that we can break down people who saw card vs people who just saw shorter grid width
      storePageEvent(PE_TYPE.LOADED, PE_KEYS.PERFORMER_INFO_CARD_LOADED, "");
    }
  }, [hasEnoughData, storePageEvent]);

  const resolvedSectionClasses =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerInfoCardSmallerText,
      sectionMutedClasses,
      sectionMutedSmallClasses,
      () => !!hasEnoughData,
    ) ?? sectionMutedClasses;

  if (!hasEnoughData) {
    return null;
  }

  const performerName = model.categorySummary.categoryName;

  return (
    <div className={cardClasses.root}>
      <div className={cardClasses.content}>
        {!!spotifyTopTrackId && <SpotifyEmbeddedPlayer trackId={spotifyTopTrackId} className={spotifyPlayerStyle} />}
        {!!youTubeVideoId && (
          <div className={videoContainerStyle}>
            <EmbeddedYoutubePlayer videoId={youTubeVideoId} />
          </div>
        )}
        <RemoveFeature
          id={FeatureSetEnum.PerformerMoveSocialIconsToBottomOfPerformerCard}
          recordType={RecordParticipationTypes.IN_VIEW}
          feature={() => <PerformerCardSocialLinks />}
        />
        {!!catalogSourcedGenre || genres ? (
          <div className={genreTagContainerStyle}>
            <GenreTags genres={catalogSourcedGenre ? [catalogSourcedGenre] : (genres ?? [])} />
          </div>
        ) : null}
        {bio?.length ? (
          <RemoveFeature
            id={FeatureSetEnum.TMINTLRemoveBio}
            canParticipate={() => !isNaCobrands(appContext)}
            recordType={RecordParticipationTypes.IN_VIEW}
            feature={() => (
              <div className={resolvedSectionClasses}>
                <TruncateText
                  isModal
                  modalTitle={performerName ?? ""}
                  maxLines={maxLinesForBio}
                  pageEventArgs={[PE_TYPE.CLICK, PE_KEYS.PERFORMER_INFO_CARD_BIO_SEE_MORE, performerName ?? ""]}
                  text={bio}
                />
              </div>
            )}
          />
        ) : null}
        {!!showDuration && (
          <RemoveFeature
            id={FeatureSetEnum.TMPerformerRemovePlaybillShowDuration}
            canParticipate={() => !!showDuration}
            recordType={RecordParticipationTypes.IN_VIEW}
            feature={() => <TheaterRuntime durationString={showDuration!} className={theaterRuntimeMargin} />}
          />
        )}
        {!!hasReigningChampionData && <ReigningChampion reigningChampionData={reigningChampions} />}
        {!!categoryUserGeneratedImages && (
          <UserContentCarousel images={categoryUserGeneratedImages} categoryName={performerName ?? ""} />
        )}
        <AddFeature
          id={FeatureSetEnum.PerformerMoveSocialIconsToBottomOfPerformerCard}
          recordType={RecordParticipationTypes.IN_VIEW}
          feature={() => <PerformerCardSocialLinks className={socialLinksStyle} />}
        />
      </div>
    </div>
  );
});
PerformerInfoCardMobile.displayName = "PerformerInfoCardMobile";
